import { useEffect, useState } from "react";
import {
  Link,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { useIntl } from "react-intl";
import sanitizeHtml from "sanitize-html";

import MakulaLogo from "@shared/svg/makula-logo.svg?react";
import MakulaLogoFull from "@shared/svg/makula.svg?react";
import Machine from "@shared/svg/machine-stroke.svg?react";
import External from "@shared/svg/external.svg?react";
import Ticket from "@shared/svg/ticket.svg?react";
import Communicate from "@shared/svg/communicate.svg?react";
import Documentation from "@shared/svg/documentation.svg?react";
import AppHeader from "@shared/ui/AppHeader";
import { handleUrl } from "@shared/utils/handleUrl";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Button, { BUTTON_VARIANTS } from "@shared/ui/Button";

import { Loading } from "#/src/components/general";
import { useGetOemBySlug } from "#/src/services/auth";

import { LoginPortalModal } from "#/src/features/PortalLanding/LoginPortalModal";
import { RequestAccessModal } from "#/src/features/PortalLanding/RequestAccessModal";
import { ForgotPasswordModal } from "#/src/features/PortalLanding/ForgotPasswordModal";
import { useAuth } from "~/components/general";
import { JWT } from "~/apollo/_config";

interface WelcomeParams {
  slug: string;
}

type ModalType = "login" | "signup" | "forgot-password" | "request" | null;

const PortalLanding = () => {
  const { slug } = useParams<WelcomeParams>();
  const { messages, formatMessage } = useIntl();
  const { oem, error, loading } = useGetOemBySlug(slug);
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [redirect, setRedirect] = useState<string | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const authToken = localStorage.getItem(JWT.LOCAL_STORAGE.TOKEN.NAME);

  useEffect(() => {
    if (authToken) {
      navigate("/app");
    }
  }, []);

  // Open request modal if URL contains request=true
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("request") === "true") {
      setActiveModal("request");
    }
  }, [location.search]);

  //redirect to workspace if user logged in and have access
  useEffect(() => {
    if (user && oem) {
      const currentPortal = user?.portals?.find(
        (portal) => portal?.name === oem?.name,
      );
      if (currentPortal && currentPortal.linkedConnection) {
        navigate(`/app/workspace/${currentPortal.linkedConnection}`);
      }
    }
  }, [oem, user, navigate]);

  if (redirect) {
    return <Navigate to={redirect} state={{ welcome: true }} replace />;
  }

  if (error) {
    return <ErrorPage code={error.message || "500"} />;
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <AppHeader padding="px-2xl py-lg">
        <div
          onClick={() => window.open("https://makula.io")}
          className="flex-col content-center cursor-pointer"
        >
          <BodyText
            className="text-center text-[10px]"
            size={BODY_TEXT_SIZES.X_SMALL}
            color="text-tertiary"
          >
            {messages.customerPortals["welcome"]["poweredBy"]}
          </BodyText>
          <MakulaLogoFull className="w-fit h-[27px]" />
        </div>
        <div className="hidden sm:flex gap-x-sm">
          <LoginButton onClick={() => setActiveModal("login")} />
          <RequestPortalButton onClick={() => setActiveModal("request")} />
          <LoginPortalModal
            isOpen={activeModal === "login"}
            onClose={() => setActiveModal(null)}
            onRequestClick={() => setActiveModal("request")}
            onForgotPasswordClick={() => setActiveModal("forgot-password")}
            organizationId={oem?._id}
            onLoginSuccess={setRedirect}
            portalName={oem?.name}
          />
          <ForgotPasswordModal
            isOpen={activeModal === "forgot-password"}
            onClose={() => setActiveModal(null)}
            onFooterLinkClick={() => setActiveModal("login")}
          />
          <RequestAccessModal
            isOpen={activeModal === "request"}
            onClose={() => setActiveModal(null)}
            onLoginClick={() => setActiveModal("login")}
            oemId={oem?._id}
          />
        </div>
      </AppHeader>
      <div className="flex flex-col">
        <div className="relative w-full md:h-[148px] h-[80px]">
          <img
            src={oem.coverPhoto || "/images/default/cover-photo.png"}
            alt="Cover photo"
            className="object-cover w-full h-full"
            loading="eager"
          />
        </div>
      </div>
      <div
        className={`w-full grid gap-4 px-4 lg:px-0 min-h-[calc(100vh-160px)] md:min-h-[calc(100vh-228px)] ${
          oem?.description?.trim() || oem?.website?.trim()
            ? "grid-cols-1 lg:grid-cols-[1fr_minmax(0,770px)_minmax(0,400px)_1fr] xl:grid-cols-[1fr_770px_580px_1fr]"
            : "grid-cols-1 lg:grid-cols-1 lg:justify-items-center lg:items-start"
        }`}
      >
        {((oem?.description?.trim() || oem?.website?.trim()) && (
          <div className="hidden lg:block" />
        )) ||
          null}
        <div
          className={
            !(oem?.description?.trim() || oem?.website?.trim())
              ? "w-full max-w-[770px]"
              : ""
          }
        >
          {oem?.brandLogo ? (
            <img
              src={oem?.brandLogo}
              className="rounded-lg object-cover bg-white border border-solid border-primary mt-[-60px] mb-4 relative z-10"
              alt="brand logo"
              style={{ width: "120px", height: "120px", borderWidth: "0.5px" }}
            />
          ) : (
            <div
              className="bg-secondary flex items-center justify-center rounded-lg flex-shrink-0 mt-[-60px] relative z-10"
              style={{ width: "120px", height: "120px" }}
            >
              <MakulaLogo className="w-fit h-4xl flex-shrink-0" />
            </div>
          )}
          <div className="mb-4">
            <BodyText
              size={BODY_TEXT_SIZES.MEDIUM}
              color="text-primary"
              className="text-[24px] !font-bold mt-2"
            >
              {formatMessage(
                {
                  id: "customerPortals.welcome.title",
                  defaultMessage: messages.customerPortals["welcome"][
                    "title"
                  ] as string,
                },
                { name: oem?.name || "" },
              )}
            </BodyText>
            <BodyText
              size={BODY_TEXT_SIZES.SMALL}
              color="text-secondary"
              className="mb-6"
            >
              {messages.customerPortals["welcome"]["subtitle"]}
            </BodyText>
            <div className="flex flex-col sm:flex-row gap-2">
              <RequestPortalButton onClick={() => setActiveModal("request")} />
              <LoginButton onClick={() => setActiveModal("login")} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FeatureCard
              img={<Machine />}
              heading={
                messages.customerPortals["welcome"]["features"][
                  "viewSharedAssets"
                ]["title"] as string
              }
              text={
                messages.customerPortals["welcome"]["features"][
                  "viewSharedAssets"
                ]["description"] as string
              }
            />
            <FeatureCard
              img={<Ticket />}
              heading={
                messages.customerPortals["welcome"]["features"][
                  "raiseSupportRequests"
                ]["title"] as string
              }
              text={
                messages.customerPortals["welcome"]["features"][
                  "raiseSupportRequests"
                ]["description"] as string
              }
            />
            <FeatureCard
              img={<Communicate />}
              heading={
                messages.customerPortals["welcome"]["features"][
                  "communicateTrackProgress"
                ]["title"] as string
              }
              text={
                messages.customerPortals["welcome"]["features"][
                  "communicateTrackProgress"
                ]["description"] as string
              }
            />
            <FeatureCard
              img={<Documentation />}
              heading={
                messages.customerPortals["welcome"]["features"][
                  "accessDocumentsManuals"
                ]["title"] as string
              }
              text={
                messages.customerPortals["welcome"]["features"][
                  "accessDocumentsManuals"
                ]["description"] as string
              }
            />
          </div>
        </div>
        {(oem?.description?.trim() || oem?.website?.trim()) && (
          <div className="pt-6 px-0 lg:px-6 border-0 lg:border-l-2 border-t-2 lg:border-t-0 border-solid border-gray-5 mt-6 lg:mt-0">
            {oem?.description && (
              <>
                <BodyText
                  size={BODY_TEXT_SIZES.SMALL}
                  className="!font-bold mb-2"
                >
                  {messages.customerPortals["welcome"]["about"]}
                </BodyText>
                <div
                  className="text-sm text-secondary mb-4 max-w-full lg:max-w-[380px]"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(oem?.description || "", {
                      allowedTags: [
                        "b",
                        "i",
                        "em",
                        "strong",
                        "p",
                        "br",
                        "ul",
                        "ol",
                        "li",
                        "a",
                      ],
                      allowedAttributes: {
                        a: ["href", "target", "rel"],
                      },
                    }),
                  }}
                />
              </>
            )}
            {oem?.website?.trim() && (
              <a
                href={handleUrl(oem?.website)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center cursor-pointer"
              >
                <External />{" "}
                <BodyText
                  size={BODY_TEXT_SIZES.X_SMALL}
                  color="text-brand"
                  className="ml-1 !font-bold"
                >
                  {messages.customerPortals["welcome"]["website"]}
                </BodyText>
              </a>
            )}
          </div>
        )}
      </div>
    </>
  );
};

const FeatureCard = ({
  img,
  heading,
  text,
}: {
  img: React.ReactNode;
  heading: string;
  text: string;
}) => (
  <div className="flex gap-x-4 p-4 w-full border border-solid border-gray-10 rounded-[5px]">
    <div className="w-6 h-6 flex-shrink-0">{img}</div>
    <div>
      <BodyText size={BODY_TEXT_SIZES.SMALL} className="!font-bold mb-2">
        {heading}
      </BodyText>
      <BodyText color="text-secondary" size={BODY_TEXT_SIZES.X_SMALL}>
        {text}
      </BodyText>
    </div>
  </div>
);

const RequestPortalButton = ({ onClick }: { onClick: () => void }) => {
  const { messages } = useIntl();
  return (
    <Button
      variant={BUTTON_VARIANTS.PRIMARY}
      text={
        messages.customerPortals["welcome"]["requestPortalAccess"] as string
      }
      className="mr-2"
      onClick={onClick}
    />
  );
};

const LoginButton = ({ onClick }: { onClick: () => void }) => {
  const { messages } = useIntl();
  return (
    <Button
      variant={BUTTON_VARIANTS.OUTLINE}
      text={messages.customerPortals["welcome"]["login"] as string}
      onClick={onClick}
    />
  );
};

const errorPage = (code: string) => {
  const errorCode = code.substr(0, 3);
  const errorFamily = code.substr(0, 2);
  const errorObj = {
    statusCode: errorCode,
    content: "Something went wrong",
  };
  switch (errorFamily) {
    case "40":
      if (errorCode === "403") {
        errorObj.content = "The requested resource is Forbidden";
      } else {
        errorObj.content =
          "The requested resource could not be found but may be available again in the future.";
      }
      break;
    case "50":
      errorObj.content =
        "An unexpected condition was encountered. Our service team has been dispatched to bring it back online.";
      break;
    default:
      break;
  }
  return errorObj;
};

const ErrorPage = ({ code }: { code: string }) => {
  const { statusCode, content } = errorPage(code);
  return (
    <section className="errorPage">
      <hgroup className="errorPage-hgroup">
        <h1>{statusCode}</h1>
        <h2>We got a problem</h2>
      </hgroup>
      <div className="errorPage-text">
        <p className="lead">{content}</p>
        <p>
          {/* @ts-ignore */}
          <Link to="/">Click here</Link> you will be redirected to the home page
        </p>
      </div>
    </section>
  );
};

export default PortalLanding;
